/* Copyright (c) 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback } from "react";
import { theme } from "@nordcloud/gnui";
import {
  useGetCloudAccountsLazyQuery,
  CloudAccountStatus,
  GetCloudAccountsQuery,
} from "~/generated/graphql";
import { ROUTES } from "~/routing/routes";
import { isNotNil } from "~/tools/filter";
import { StatusChartItem } from "../components/types";
import { useFetchAllPages, REQUEST_LIMIT } from "./useFetchAllPages";

type CloudAccountListItem = {
  id: string;
  status: CloudAccountStatus;
};

const ACCOUNT_STATUS_DEFINITIONS: ReadonlyArray<{
  status: CloudAccountStatus;
  label: string;
  color: string;
}> = [
  {
    status: CloudAccountStatus.AccessError,
    label: "Access Error",
    color: theme.color.background.error,
  },
  {
    status: CloudAccountStatus.AccessMissing,
    label: "Access Missing",
    color: theme.color.background.error,
  },
  {
    status: CloudAccountStatus.RegionsUnavailable,
    label: "Region Unavailable",
    color: theme.color.background.warning,
  },
  {
    status: CloudAccountStatus.InProgress,
    label: "In Progress",
    color: theme.color.background.info,
  },
  {
    status: CloudAccountStatus.AccessOk,
    label: "Connected",
    color: theme.color.background.success,
  },
];

export function useDashboardAccounts() {
  const [getData, { data, loading: firstPageLoading, fetchMore, error }] =
    useGetCloudAccountsLazyQuery();

  const getCloudAccounts = useCallback(
    () =>
      getData({
        variables: {
          page: 1,
          limit: REQUEST_LIMIT,
        },
      }),
    [getData]
  );

  const { items, loading } = useFetchAllPages<
    CloudAccountListItem,
    GetCloudAccountsQuery
  >({
    firstPageLoading,
    firstPageResult: data,
    pageInfo: data?.cloudAccounts?.pageInfo,
    fetchMore,
    mapFunc: extractQueryResult,
  });

  return {
    getCloudAccounts,
    accountsStatus: getCloudAccountsStatus(items),
    loading,
    error,
    count: data?.cloudAccounts?.pageInfo.count,
  } as const;
}

const extractQueryResult = (
  data: GetCloudAccountsQuery | undefined
): CloudAccountListItem[] => {
  return (data?.cloudAccounts?.result ?? [])
    .filter(isNotNil)
    .map(({ id, status }) => ({
      id,
      status,
    }));
};

const getCloudAccountsStatus = (
  list: CloudAccountListItem[]
): StatusChartItem[] => {
  const counts = list.reduce<Partial<Record<CloudAccountStatus, number>>>(
    (acc, { status }) => {
      acc[status] = (acc[status] ?? 0) + 1;

      return acc;
    },
    {}
  );

  return ACCOUNT_STATUS_DEFINITIONS.map(({ status, label, color }) => ({
    label,
    count: counts[status] ?? 0,
    color,
    link: buildCloudAccountStatusLink(status),
  }));
};

const buildCloudAccountStatusLink = (status: CloudAccountStatus): string => {
  return ROUTES.accounts.index + `?cloudAccountStatuses[]=${status}`;
};
